import { cn } from '@pesto/utils';

import type { EnvBadgeVariant } from './Navigation/Navigation.types';

import { Badge } from './Badge';

export interface EnvBadgeConfig {
  label: string;
  variant: EnvBadgeVariant;
  className?: string;
}

export interface GetEnvBadgeConfigOptions {
  classNameByLabel?: Record<string, string>;
}

type EnvLabel = 'Staging' | 'Dev' | 'Local';

const VARIANT_BY_LABEL: Partial<Record<EnvLabel, EnvBadgeVariant>> = {
  Staging: 'warning',
  Dev: 'secondary',
};

const DEFAULT_CLASS_BY_LABEL: Partial<Record<EnvLabel, string>> = {
  Local: 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15',
};

const getEnvLabel = (): EnvLabel | null => {
  const viteEnv = import.meta.env.VITE_ENV;
  const isProd = import.meta.env.VITE_PROD;

  if (viteEnv === 'production' || isProd) return null;

  if (viteEnv === 'staging') return 'Staging';

  if (viteEnv === 'development' || viteEnv === 'dev') return 'Dev';

  if (import.meta.env.MODE === 'development') return 'Local';

  return null;
};

export const getEnvBadgeConfig = (options?: GetEnvBadgeConfigOptions): EnvBadgeConfig | null => {
  const label = getEnvLabel();

  if (!label) return null;
  const classNameByLabel = options?.classNameByLabel ?? DEFAULT_CLASS_BY_LABEL;

  return {
    label,
    variant: VARIANT_BY_LABEL[label] ?? 'outline',
    className: classNameByLabel[label],
  };
};

interface EnvBadgeProps {
  className?: string;
  config?: EnvBadgeConfig | null;
}

export const EnvBadge = ({ className, config: configOverride }: EnvBadgeProps) => {
  const config = configOverride === undefined ? getEnvBadgeConfig() : configOverride;

  if (!config) return null;

  return (
    <Badge variant={config.variant} className={cn('uppercase tracking-wider', config.className, className)}>
      {config.label}
    </Badge>
  );
};
